import { useMemo } from "react";
import moment from "moment";
import { Col, Row, Table } from "react-bootstrap";
import {
  formattedValue,
  getInventoryCategories,
  getInventoryCategoryById,
  isAdmin,
  isHospitalAdmin,
  isManager,
  isManagerAndAbove,
} from "../../Services/common";
import { getColorFromStatus } from "../../Utils/appUtils";
import { changeDateFormatForDisplay, changeDateTimeFormatForDisplay } from "../../Utils/dateUtils";
import * as stringUtils from "../../Utils/stringUtils";
import IconResource from "../../Wrapper/IconResource/IconResource";
import ImageResource from "../../Wrapper/ImageResource/ImageResource";
import Button from "../_widgets/Button/Button";
import DateRangePicker from "../_widgets/DateRangePicker/DateRangePicker";
import Input from "../_widgets/Input/Input";
import MainLoader from "../_widgets/Loader/MainLoader";
import MultiSelectDropdown from "../_widgets/MultiSelect/MultiSelectDropdown";
import NavBar from "../_widgets/NavBar/NavBar";
import CustomPagination from "../_widgets/Pagination/Pagination";
import Text from "../_widgets/Text/Text";
import CustomTooltip from "../_widgets/Tooltip/CustomTooltip";
import AnimatedPage from "../AnimatedPage/AnimatedPage";
import AddCommentModal from "./AddCommentModal";
import AddConsumeModal from "./AddConsumeModal";
import AddEditModal from "./AddEditModal";
import AddStockModal from "./AddStockModal";
import HistoryModal from "./HistoryModal";
import "./Inventory.scss";

const InventoryUI = ({
  modalShow,
  importListing,
  exportList,
  importModal,
  importList,
  setSelectedRows,
  setImportModal,
  openImportModal,
  setModalShow,
  statusHandler,
  handleClose,
  inventoryList,
  addInventoryData,
  setAddInventoryData,
  addInventory,
  editInventory,
  onPageChange,
  dates,
  setDates,
  datePickerStartRef,
  datePickerEndRef,
  searchQuery,
  setSearchQuery,
  loading,
  buttonLoading,
  Action,
  selectedRows,
  getInventoryList,
  modalAddStockShow,
  setModalAddStockShow,
  addInventoryStockData,
  setAddInventoryStockData,
  addInventoryStock,
  handleAddStockClose,
  modalAddConsumeShow,
  setModalAddConsumeShow,
  addInventoryConsumeData,
  setAddInventoryConsumeData,
  handleAddConsumeClose,
  addInventoryConsume,
  modalAddCommentShow,
  setModalAddCommentShow,
  addInventoryComment,
  setAddInventoryComment,
  handleAddCommentClose,
  addInventoryComments,
  modalHistoryShow,
  setModalHistoryShow,
  addInventoryHistory,
  setAddInventoryHistory,
  handleHistoryClose,
  handleHistoryPageChange,
  selectedTypes,
  setSelectedTypes,
  handleTypeDropdownChange,
  handleStatusDropdownChange,
  selectedStatus,
  setSelectedStatus,
  historyData,
}) => {
  const stats = useMemo(() => {
    const list = inventoryList?.data || [];
    const total = inventoryList?.total || list.length;
    const inStock = list.filter((i) => (parseFloat(i.stock) || 0) > 0).length;
    const outOfStock = list.filter((i) => (parseFloat(i.stock) || 0) <= 0).length;
    const expired = list.filter(
      (i) => i.expiry_date && moment(i.expiry_date).isBefore(moment(), "day"),
    ).length;
    const expiringSoon = list.filter((i) => {
      if (!i.expiry_date) return false;
      const exp = moment(i.expiry_date);
      return (
        exp.isSameOrAfter(moment(), "day") &&
        exp.isSameOrBefore(moment().add(30, "days"), "day")
      );
    }).length;

    return { total, inStock, outOfStock, expired, expiringSoon };
  }, [inventoryList]);

  return (
    <AnimatedPage>
      <NavBar
        button={isManagerAndAbove() ? "Add Item" : null}
        buttonIcon="inventory"
        heading="Inventory"
        onClick={() => {
          document.body.classList.toggle("side_active");
          document.body.classList.toggle("side_active_mb");
        }}
        setModalShow={setModalShow}
        Icon="menu"
      />

      <AddEditModal
        modalShow={modalShow}
        setModalShow={setModalShow}
        handleClose={handleClose}
        addInventoryData={addInventoryData}
        addInventory={addInventory}
        editInventory={editInventory}
        buttonLoading={buttonLoading}
        setAddInventoryData={setAddInventoryData}
      />

      <AddStockModal
        modalShow={modalAddStockShow}
        setModalShow={setModalAddStockShow}
        handleClose={handleAddStockClose}
        addInventoryData={addInventoryStockData}
        addInventory={addInventoryStock}
        buttonLoading={buttonLoading}
        setAddInventoryData={setAddInventoryStockData}
      />

      <AddConsumeModal
        modalShow={modalAddConsumeShow}
        setModalShow={setModalAddConsumeShow}
        handleClose={handleAddConsumeClose}
        addInventoryData={addInventoryConsumeData}
        addInventory={addInventoryConsume}
        buttonLoading={buttonLoading}
        setAddInventoryData={setAddInventoryConsumeData}
      />

      <AddCommentModal
        modalShow={modalAddCommentShow}
        setModalShow={setModalAddCommentShow}
        handleClose={handleAddCommentClose}
        addInventoryData={addInventoryComment}
        addInventory={addInventoryComments}
        buttonLoading={buttonLoading}
        setAddInventoryData={setAddInventoryComment}
      />

      <HistoryModal
        modalShow={modalHistoryShow}
        setModalShow={setModalHistoryShow}
        handleClose={handleHistoryClose}
        addInventoryData={addInventoryComment}
        addInventory={addInventoryHistory}
        buttonLoading={buttonLoading}
        setAddInventoryData={setAddInventoryHistory}
        handleHistoryPageChange={handleHistoryPageChange}
        loading={loading}
        historyData={historyData}
      />

      <div className="inventory-container">
        <div className="quick-stats-grid">
          <div className="stat-card stat-total">
            <div className="stat-content">
              <span className="stat-label">Total Items</span>
              <span className="stat-value">{stats.total}</span>
            </div>
            <div className="stat-icon-wrapper">
              <IconResource type="inventory" size={22} color="#3b82f6" />
            </div>
          </div>

          <div className="stat-card stat-in-stock">
            <div className="stat-content">
              <span className="stat-label">In Stock</span>
              <span className="stat-value">{stats.inStock}</span>
            </div>
            <div className="stat-icon-wrapper">
              <IconResource type="tick" size={22} color="#10b981" />
            </div>
          </div>

          <div className="stat-card stat-out-of-stock">
            <div className="stat-content">
              <span className="stat-label">Out of Stock</span>
              <span className="stat-value">{stats.outOfStock}</span>
            </div>
            <div className="stat-icon-wrapper">
              <IconResource type="alert" size={22} color="#f59e0b" />
            </div>
          </div>

          <div className="stat-card stat-expired">
            <div className="stat-content">
              <span className="stat-label">Expired</span>
              <span className="stat-value">{stats.expired}</span>
            </div>
            <div className="stat-icon-wrapper">
              <IconResource type="delete" size={22} color="#ef4444" />
            </div>
          </div>

          <div className="stat-card stat-expiring-soon">
            <div className="stat-content">
              <span className="stat-label">Expiring Soon (30d)</span>
              <span className="stat-value">{stats.expiringSoon}</span>
            </div>
            <div className="stat-icon-wrapper">
              <IconResource type="calendar" size={22} color="#f97316" />
            </div>
          </div>
        </div>

        <div className="patient_list white_card">
          <div className="top_bar">
            <Text type="h2" text="" />
            <Row className="search_filter">
              <Col lg={2} xs={6} md={5}>
                <div className="bg_grey d-flex align-items-center justify-content-center position-relative px-2 py-1">
                  {selectedStatus.length > 0 ? (
                    <div className="round-notification">{selectedStatus.length}</div>
                  ) : (
                    ""
                  )}

                  <MultiSelectDropdown
                    items={[
                      {
                        label: "ACTIVE",
                        value: "ACTIVE",
                      },
                      {
                        label: "INACTIVE",
                        value: "INACTIVE",
                      },
                    ]}
                    name={
                      <div className="d-flex align-items-center gap-1">
                        <IconResource type={"filter"} size={12} color={"var(--primary)"} />
                        <small
                          style={{
                            fontSize: "12px",
                            fontWeight: "bold",
                            color: "var(--primary)",
                            cursor: "pointer",
                          }}
                          className=""
                        >
                          Status
                        </small>
                        {selectedStatus.length > 0 ? (
                          <IconResource
                            type={"cross"}
                            size={12}
                            onClick={() => setSelectedStatus([])}
                          />
                        ) : (
                          ""
                        )}
                      </div>
                    }
                    selectedOptions={selectedStatus}
                    onChange={handleStatusDropdownChange}
                  />
                </div>
              </Col>
              <Col lg={2} xs={6} md={5}>
                <div className="bg_grey d-flex align-items-center justify-content-center position-relative px-2 py-1">
                  {selectedTypes.length > 0 ? (
                    <div className="round-notification">{selectedTypes.length}</div>
                  ) : (
                    ""
                  )}

                  <MultiSelectDropdown
                    items={getInventoryCategories()}
                    name={
                      <div className="d-flex align-items-center gap-1">
                        <IconResource type={"filter"} size={12} color={"var(--primary)"} />
                        <small
                          style={{
                            fontSize: "12px",
                            fontWeight: "bold",
                            color: "var(--primary)",
                            cursor: "pointer",
                          }}
                          className=""
                        >
                          Category
                        </small>
                        {selectedTypes.length > 0 ? (
                          <IconResource
                            type={"cross"}
                            size={12}
                            onClick={() => setSelectedTypes([])}
                          />
                        ) : (
                          ""
                        )}
                      </div>
                    }
                    selectedOptions={selectedTypes}
                    onChange={handleTypeDropdownChange}
                  />
                </div>
              </Col>
              <Col lg={2} xs={12} md={4}>
                <div className="position-relative">
                  <Input
                    placeholder="Search"
                    id="searchInput"
                    suffixIcon="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClickIconSuff={() => getInventoryList()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        getInventoryList();
                      }
                    }}
                    iconTooltip="Search"
                  />
                </div>
              </Col>
              <Col lg={6} xs={12} md={8}>
                <div className="date_range_picker">
                  <DateRangePicker
                    startDate={dates?.startDate}
                    setStartDate={(startDate) => setDates({ ...dates, startDate })}
                    endDate={dates?.endDate}
                    setEndDate={(endDate) => setDates({ ...dates, endDate })}
                    onChange={(startDate, endDate) => setDates({ startDate, endDate })}
                    datePickerStartRef={datePickerStartRef}
                    datePickerEndRef={datePickerEndRef}
                    showDateShortcuts={true}
                  />
                </div>
              </Col>
            </Row>
          </div>

          <div className="custom_table position-relative">
            {loading ? (
              <MainLoader />
            ) : inventoryList.data?.length > 0 ? (
              <>
                <div>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Category</th>
                        <th>Stock</th>
                        <th>Expiry Date</th>
                        <th>Status</th>
                        <th>Remarks</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {inventoryList?.data?.map((data, index) => {
                        const isExpired =
                          data?.expiry_date &&
                          moment(data.expiry_date).isBefore(moment(), "day");
                        const isExpiringSoon =
                          !isExpired &&
                          data?.expiry_date &&
                          moment(data.expiry_date).isSameOrBefore(
                            moment().add(30, "days"),
                            "day",
                          );

                        return (
                          <tr key={index}>
                            <td>
                              <CustomTooltip text={data.name} placement={"top"}>
                                {data.name}
                              </CustomTooltip>
                            </td>

                            <td>
                              <CustomTooltip
                                text={getInventoryCategoryById(data?.inventory_category_id)}
                                placement={"top"}
                              >
                                {getInventoryCategoryById(data?.inventory_category_id)}
                              </CustomTooltip>
                            </td>
                            <td>
                              <CustomTooltip text={data.stock} placement={"top"}>
                                {data?.stock !== null
                                  ? formattedValue(data?.stock, false, true)
                                  : "-"}
                              </CustomTooltip>
                            </td>

                            <td>
                              {data?.expiry_date ? (
                                isExpired ? (
                                  <CustomTooltip text="Expired item" placement="top">
                                    <span className="expiry-badge expired">
                                      <IconResource type="alert" size={12} color="#dc2626" />
                                      {changeDateFormatForDisplay(data.expiry_date)}
                                    </span>
                                  </CustomTooltip>
                                ) : isExpiringSoon ? (
                                  <CustomTooltip
                                    text="Expiring within 30 days"
                                    placement="top"
                                  >
                                    <span className="expiry-badge expiring-soon">
                                      {changeDateFormatForDisplay(data.expiry_date)}
                                    </span>
                                  </CustomTooltip>
                                ) : (
                                  <span className="expiry-badge valid">
                                    {changeDateFormatForDisplay(data.expiry_date)}
                                  </span>
                                )
                              ) : (
                                <span className="expiry-badge none">-</span>
                              )}
                            </td>

                            <td>
                              {isAdmin() || isHospitalAdmin() || isManager() ? (
                                <CustomTooltip
                                  type="popover"
                                  button={
                                    <div className="p-1">
                                      <Button
                                        isLoading={buttonLoading}
                                        onClick={() => statusHandler(data)}
                                        buttonType="primary"
                                        text={
                                          data.status === "ACTIVE" ? "Deactivate" : "Activate"
                                        }
                                      />
                                    </div>
                                  }
                                >
                                  <div
                                    className={`status status__${getColorFromStatus(data.status)}`}
                                  >
                                    <Text text={stringUtils.capitalizeFirstLetter(data.status)} />
                                  </div>
                                </CustomTooltip>
                              ) : (
                                <div
                                  className={`status status__${getColorFromStatus(data.status)}`}
                                >
                                  <Text text={stringUtils.capitalizeFirstLetter(data.status)} />
                                </div>
                              )}
                            </td>

                            <td>
                              <CustomTooltip text={data.remarks} placement={"top"}>
                                {data?.remarks || "-"}
                              </CustomTooltip>
                            </td>
                            <td>
                              <CustomTooltip
                                text={changeDateTimeFormatForDisplay(data.created_date)}
                                placement="top"
                              >
                                {data.created_date
                                  ? changeDateTimeFormatForDisplay(data.created_date)
                                  : "-"}
                              </CustomTooltip>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-2 justify-content-center">
                              {isAdmin() || isHospitalAdmin() || isManager() ? (
                                <CustomTooltip text="Edit" placement={"top"}>
                                  <Button
                                    className="p-1 bg-transparent border-0"
                                    icon={<IconResource type="edit" color="var(--light-text)" />}
                                    onClick={() => Action(data, "edit")}
                                  />
                                </CustomTooltip>
                              ) : null}
                              <CustomTooltip text="Add Stock" placement={"top"}>
                                <Button
                                  className="p-1 bg-transparent border-0"
                                  icon={<IconResource type="plus" color="var(--light-text)" />}
                                  onClick={() => Action(data, "addStock")}
                                />
                              </CustomTooltip>
                              <CustomTooltip text="Consume Stock" placement={"top"}>
                                <Button
                                  className="p-1 bg-transparent border-0"
                                  icon={<IconResource type="dash" color="var(--light-text)" />}
                                  onClick={() => Action(data, "addConsume")}
                                />
                              </CustomTooltip>

                              <CustomTooltip text="Add Comment" placement={"top"}>
                                <Button
                                  className="p-1 bg-transparent border-0"
                                  icon={<IconResource type="comment" color="var(--light-text)" />}
                                  onClick={() => Action(data, "addComment")}
                                />
                              </CustomTooltip>

                              <CustomTooltip text="History" placement={"top"}>
                                <Button
                                  className="p-1 bg-transparent border-0"
                                  icon={<IconResource type="history" color="var(--light-text)" />}
                                  onClick={() => Action(data, "history")}
                                />
                              </CustomTooltip>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              <CustomPagination
                activePage={inventoryList?.current_page}
                onPageChange={onPageChange}
                pageLength={inventoryList?.per_page}
                totalData={inventoryList?.total}
                lastPage={inventoryList?.last_page}
              />
            </>
          ) : (
            <>
              <div className="no_data position-relative">
                <ImageResource type="noData" style={{ width: "300px" }} className="img-fluid" />
                <Text type="h2" text="There is no data to display. Try re-setting filters!" />
                {isAdmin() ||
                  isHospitalAdmin() ||
                  (isManager() && (
                    <Button
                      buttonType="primary"
                      text="Add Item"
                      suffixIcon={<IconResource type="addInventory" />}
                      onClick={() => setModalShow(true)}
                    />
                  ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  </AnimatedPage>
);
};

export default InventoryUI;
